import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { formatRelativeTime } from '@/lib/utils';
import { useDeleteDeckGroup } from '@/queries/deck-groups';
import SquarePenIcon from '@/assets/common/square-pen.svg?react';
import LibraryIcon from '@/assets/common/library.svg?react';
import styles from './DeckGroupCard.module.scss';
import type { DeckGroup } from '@/types/deck-group';

interface DeckGroupCardProps {
  group: DeckGroup;
  index?: number;
}

export function DeckGroupCard({ group, index = 0 }: DeckGroupCardProps) {
  const navigate = useNavigate();
  const deleteDeckGroup = useDeleteDeckGroup();

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({
      to: '/quizlets/deck-groups/$groupId/edit',
      params: { groupId: group.id },
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this deck group?')) {
      deleteDeckGroup.mutate(group.id);
    }
  };

  const handleStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({
      to: '/quizlets/deck-groups/$groupId',
      params: { groupId: group.id },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        type: 'spring',
        stiffness: 100,
      }}
      style={{ height: '100%', minWidth: 0 }}
    >
      <div className={styles.deckGroupCard}>
        <div className={styles.deckGroupCard__header}>
          <h3 className={styles.deckGroupCard__title}>{group.name}</h3>
          <div className={styles.deckGroupCard__badge}>
            <LibraryIcon className={styles.deckGroupCard__badgeIcon} />
            {group.quizlets.length} Decks
          </div>
        </div>

        {group.description && (
          <p className={styles.deckGroupCard__description}>{group.description}</p>
        )}

        <div className={styles.deckGroupCard__quizlets}>
          <span className={styles.deckGroupCard__quizletsLabel}>Included:</span>
          <span className={styles.deckGroupCard__quizletsList}>
            {group.quizlets.length > 0
              ? group.quizlets.map((q) => q.name).join(', ')
              : 'No decks added'}
          </span>
        </div>

        <div className={styles.deckGroupCard__stats}>
          <div className={styles.deckGroupCard__badge}>
            <SquarePenIcon className={styles.deckGroupCard__badgeIcon} />
            {group.total_questions} Questions
          </div>
        </div>

        <div className={styles.deckGroupCard__footer}>
          <div className={styles.deckGroupCard__meta}>
            <span className={styles.deckGroupCard__metaItem}>
              Updated {formatRelativeTime(group.updated_at)}
            </span>
          </div>

          <div className={styles.deckGroupCard__actions}>
            <button
              onClick={handleStart}
              className={`${styles.deckGroupCard__actionButton} ${styles['deckGroupCard__actionButton--primary']}`}
              disabled={group.total_questions === 0}
            >
              Start
            </button>
            <button
              onClick={handleEdit}
              className={styles.deckGroupCard__actionButton}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className={`${styles.deckGroupCard__actionButton} ${styles['deckGroupCard__actionButton--danger']}`}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
